import gql from "graphql-tag";
import React, { useCallback, useRef, useState } from "react";

import { SecondaryButton } from "~/components/common/buttons";
import { UploadFileIcon } from "~/components/common/icons";
import { useToast } from "~/components/common/ToastProvider";
import { useUploadProjectSpatialFileMutation } from "~/components/Projects/UploadSpatialFileButton.generated";

export const UPLOAD_PROJECT_SPATIAL_FILE_MUTATION = gql`
  mutation UploadProjectSpatialFile($projectId: ID!, $file: Upload!) {
    uploadProjectSpatialFile(projectId: $projectId, file: $file) {
      success
      message
      uploadPath
    }
  }
`;

interface UploadSpatialFileButtonProps {
  projectId: string;
  onSuccess?: () => void;
}

export const UploadSpatialFileButton = React.memo(
  function UploadSpatialFileButton({
    projectId,
    onSuccess,
  }: UploadSpatialFileButtonProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadMutation] = useUploadProjectSpatialFileMutation();
    const { showSuccess, showError } = useToast();

    const handleFileSelect = useCallback(
      async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file extension
        const ext = file.name.toLowerCase().split(".").pop();
        if (ext !== "kml" && ext !== "kmz") {
          showError("Invalid file type. Only .kml and .kmz files are allowed.");
          return;
        }

        // Validate file size (50MB max)
        const maxSize = 50 * 1024 * 1024; // 50MB in bytes
        if (file.size > maxSize) {
          showError("File size exceeds 50MB limit.");
          return;
        }

        setUploading(true);

        try {
          const result = await uploadMutation({
            variables: {
              projectId,
              file,
            },
          });

          if (result.data?.uploadProjectSpatialFile?.success) {
            showSuccess(
              result.data.uploadProjectSpatialFile.message ||
                "File uploaded successfully! Processing will begin shortly."
            );
            onSuccess?.();
          } else {
            showError(
              result.data?.uploadProjectSpatialFile?.message ||
                "Upload failed. Please try again."
            );
          }
        } catch (error) {
          console.error("Upload error:", error);
          showError("Upload failed. Please try again.");
        } finally {
          setUploading(false);
          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      },
      [projectId, uploadMutation, onSuccess, showSuccess, showError]
    );

    const handleClick = useCallback(() => {
      fileInputRef.current?.click();
    }, []);

    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          accept=".kml,.kmz"
          style={{ display: "none" }}
          onChange={handleFileSelect}
          disabled={uploading}
        />
        <SecondaryButton onClick={handleClick} disabled={uploading}>
          <UploadFileIcon />
          <span>{uploading ? "Uploading..." : "Upload KML/KMZ"}</span>
        </SecondaryButton>
      </>
    );
  }
);
